import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRegistrations } from '../services/api';
import { logout } from '../services/auth';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
    Button,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Card,
    CardContent,
    Avatar,
    Stack,
    Chip,
    Divider,
    LinearProgress
} from '@mui/material';
import { Visibility, Logout, PictureAsPdf, GridOn, TableChart, Download, CheckCircle, Cancel, HourglassEmpty, Payment } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

// Marathi translations
const translations = {
    login: 'लॉगिन',
    registrations: 'नोंदणी यादी',
    regNo: 'नोंदणी क्रमांक',
    name: 'नाव',
    dob: 'जन्मतारीख',
    city: 'शहर',
    district: 'जिल्हा',
    actions: 'क्रिया',
    viewDetails: 'तपशील पहा',
    logout: 'बाहेर पडणे',
    export: 'निर्यात करा',
    allFields: 'सर्व फील्ड',
    selected: 'निवडलेले',
    all: 'सर्व',
    search: 'शोधा',
    totalRegistrations: 'एकूण नोंदणी',
    lastUpdated: 'शेवटचे अद्यतन',
    exportOptions: 'निर्यात पर्याय',
    welcome: 'स्वागत आहे',
    dashboard: 'डॅशबोर्ड',
    paymentStatus: 'पेमेंट स्थिती',
    pending: 'प्रलंबित',
    pending_verification: 'तपासणी प्रलंबित',
    paid: 'पेमेंट पूर्ण',
    rejected: 'नाकारले',
};

const getPaymentStatusLabel = (status) => {
    switch (status) {
        case 'paid': return translations.paid;
        case 'pending_verification': return translations.pending_verification;
        case 'rejected': return translations.rejected;
        default: return translations.pending;
    }
};

const getPaymentStatusColor = (status) => {
    switch (status) {
        case 'paid': return 'success';
        case 'pending_verification': return 'warning';
        case 'rejected': return 'error';
        default: return 'default';
    }
};

// Marathi column mappings for exports
const marathiColumnNames = {
    id: 'आयडी',
    name: 'नाव',
    mobile_number: 'मोबाईल नंबर',
    dob: 'जन्मतारीख',
    dot: 'जन्म वेळ',
    education: 'शिक्षण',
    occupation: 'व्यवसाय',
    height: 'उंची (सेमी)',
    skin_color: 'त्वचेचा रंग',
    passport_photo: 'पासपोर्ट फोटो',
    full_photo: 'पूर्ण फोटो',
    dad_name: 'वडिलांचे नाव',
    mom_name: 'आईचे नाव',
    uncle_name: 'काकांचे नाव',
    guardian_phone: 'पालकांचा फोन',
    address: 'पत्ता',
    marital_status: 'वैवाहिक स्थिती',
    public_marriage: 'सार्वजनिक विवाह',
    sign_photo: 'सही फोटो',
    sr_no_autogen: 'नोंदणी क्रमांक',
    city: 'शहर',
    pincode_form_level: 'पिन कोड',
    district: 'जिल्हा',
    payment_status: 'पेमेंट स्थिती',
    payment_id: 'पेमेंट आयडी',
    payment_amount: 'पेमेंट रक्कम',
    payment_utr: 'पेमेंट यूटीआर',
    upi_screenshot: 'UPI स्क्रीनशॉट',
    created_at: 'नोंदणीची तारीख',
    updated_at: 'अद्यतन तारीख'
};

const Registrations = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const navigate = useNavigate();

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy');
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss');
        } catch {
            return dateString;
        }
    };

    const formatHeight = (height) => {
        if (!height) return '-';
        // Convert feet to cm if needed (e.g., "5.8" becomes "175 cm")
        if (height.toString().includes('.')) {
            const parts = height.toString().split('.');
            const feet = parseInt(parts[0]);
            const inches = parseInt(parts[1]);
            const totalInches = feet * 12 + inches;
            const cm = Math.round(totalInches * 2.54);
            return `${cm} सेमी`;
        }
        return `${height} सेमी`;
    };

    const columns = [
        { field: 'sr_no_autogen', headerName: translations.regNo, width: 180 },
        { field: 'name', headerName: translations.name, width: 180 },
        {
            field: 'dob',
            headerName: translations.dob,
            width: 120,
            valueFormatter: (params) => formatDate(params.value)
        },
        { field: 'city', headerName: translations.city, width: 120 },
        {
            field: 'payment_status',
            headerName: translations.paymentStatus,
            width: 160,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={getPaymentStatusLabel(params.value)}
                    color={getPaymentStatusColor(params.value)}
                />
            ),
        },
        {
            field: 'actions',
            headerName: translations.actions,
            width: 100,
            renderCell: (params) => (
                <Tooltip title={translations.viewDetails}>
                    <IconButton onClick={() => navigate(`/registrations/${params.row.id}`)}>
                        <Visibility color="primary" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await getRegistrations(
                    paginationModel.page + 1,
                    paginationModel.pageSize,
                    search
                );
                setRegistrations(data.data);
                setTotal(data.meta.total);
                setLastUpdated(new Date());
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [paginationModel, search]);

    const exportToCSV = (data, allRecords = false) => {
        const csvData = data.map(reg => {
            const row = {};
            Object.keys(reg).forEach(key => {
                if (key === 'dob' || key === 'created_at' || key === 'updated_at') {
                    row[marathiColumnNames[key] || key] = formatDateTime(reg[key]);
                } else if (key === 'height') {
                    row[marathiColumnNames[key] || key] = formatHeight(reg[key]);
                } else {
                    row[marathiColumnNames[key] || key] = reg[key];
                }
            });
            return row;
        });

        const csv = XLSX.utils.json_to_sheet(csvData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, csv, 'नोंदणी');
        XLSX.writeFile(workbook, `नोंदणी_${allRecords ? 'सर्व' : 'निवडलेले'}_${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const exportToExcel = (data, allRecords = false) => {
        const excelData = data.map(reg => {
            const row = {};
            Object.keys(reg).forEach(key => {
                if (key === 'dob' || key === 'created_at' || key === 'updated_at') {
                    row[marathiColumnNames[key] || key] = formatDateTime(reg[key]);
                } else if (key === 'height') {
                    row[marathiColumnNames[key] || key] = formatHeight(reg[key]);
                } else {
                    row[marathiColumnNames[key] || key] = reg[key];
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'नोंदणी');
        XLSX.writeFile(workbook, `नोंदणी_${allRecords ? 'सर्व' : 'निवडलेले'}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const exportToPDF = async (data, allRecords = false) => {
        const doc = new jsPDF();

        // Add title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('नोंदणी तपशील', 105, 15, { align: 'center' });

        // Add subtitle with date and record count
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`निर्यात तारीख: ${format(new Date(), 'dd/MM/yyyy HH:mm')} | एकूण नोंदणी: ${data.length}`, 105, 22, { align: 'center' });

        // Process each registration
        for (let i = 0; i < data.length; i++) {
            if (i > 0) doc.addPage();

            const reg = data[i];
            const yStart = 30;
            let y = yStart;

            // Add registration details
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(`नोंदणी क्रमांक: ${reg.sr_no_autogen}`, 14, y);
            y += 8;

            doc.setFont('helvetica', 'normal');
            Object.keys(reg).forEach(key => {
                if (key !== 'passport_photo' && key !== 'full_photo' && key !== 'sign_photo' && key !== 'id') {
                    const label = marathiColumnNames[key] || key;
                    let value = reg[key];

                    // Format dates
                    if (key === 'dob' || key === 'created_at' || key === 'updated_at') {
                        value = formatDateTime(value);
                    }
                    // Format height
                    else if (key === 'height') {
                        value = formatHeight(value);
                    }

                    doc.text(`${label}: ${value || '-'}`, 14, y);
                    y += 7;
                }
            });

            // Add images if available
            if (reg.passport_photo) {
                try {
                    const imgData = await loadImage(reg.passport_photo);
                    doc.addImage(imgData, 'JPEG', 14, y, 40, 40);
                    doc.text('पासपोर्ट फोटो', 60, y + 20);
                    y += 45;
                } catch (e) {
                    console.error('Error loading passport photo:', e);
                }
            }

            if (reg.full_photo) {
                try {
                    const imgData = await loadImage(reg.full_photo);
                    doc.addImage(imgData, 'JPEG', 14, y, 80, 80);
                    doc.text('पूर्ण फोटो', 50, y + 85);
                    y += 90;
                } catch (e) {
                    console.error('Error loading full photo:', e);
                }
            }
        }

        doc.save(`नोंदणी_${allRecords ? 'सर्व' : 'निवडलेले'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const loadImage = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    };

    const handleExport = async (format) => {
        let dataToExport = [];

        if (format.endsWith('_all')) {
            // Fetch all records for "all" export
            setLoading(true);
            try {
                const { data } = await getRegistrations(1, total, search);
                dataToExport = data.data;
            } catch (err) {
                console.error(err);
                return;
            } finally {
                setLoading(false);
            }
        } else {
            dataToExport = selectedIds.length > 0
                ? registrations.filter(reg => selectedIds.includes(reg.id))
                : registrations;
        }

        const exportType = format.replace('_all', '');

        switch (exportType) {
            case 'csv':
                exportToCSV(dataToExport, format.endsWith('_all'));
                break;
            case 'excel':
                exportToExcel(dataToExport, format.endsWith('_all'));
                break;
            case 'pdf':
                await exportToPDF(dataToExport, format.endsWith('_all'));
                break;
            default:
                break;
        }
    };

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer sx={{ p: 2, gap: 2 }}>
                <GridToolbarQuickFilter
                    placeholder={`${translations.search}...`}
                    sx={{
                        width: 300,
                        '& .MuiInput-root': {
                            borderRadius: '20px',
                            padding: '0 10px'
                        }
                    }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{translations.exportOptions}</InputLabel>
                    <Select
                        value=""
                        label={translations.exportOptions}
                        onChange={(e) => handleExport(e.target.value)}
                        startAdornment={<Download sx={{ mr: 1 }} />}
                    >
                        <MenuItem value="csv">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <TableChart fontSize="small" />
                                <span>CSV (निवडलेले)</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="excel">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <GridOn fontSize="small" />
                                <span>Excel (निवडलेले)</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="pdf">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <PictureAsPdf fontSize="small" />
                                <span>PDF (निवडलेले)</span>
                            </Stack>
                        </MenuItem>
                        <Divider />
                        <MenuItem value="csv_all">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <TableChart fontSize="small" />
                                <span>CSV (सर्व)</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="excel_all">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <GridOn fontSize="small" />
                                <span>Excel (सर्व)</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="pdf_all">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <PictureAsPdf fontSize="small" />
                                <span>PDF (सर्व)</span>
                            </Stack>
                        </MenuItem>
                    </Select>
                </FormControl>

                <Button
                    startIcon={<Logout />}
                    onClick={() => { logout(); navigate('/login'); }}
                    sx={{ ml: 'auto', borderRadius: '20px' }}
                    variant="outlined"
                    color="error"
                >
                    {translations.logout}
                </Button>
            </GridToolbarContainer>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {translations.dashboard}
                </Typography>
                <Chip
                    label={`${format(lastUpdated, 'dd/MM/yyyy HH:mm')}`}
                    size="small"
                    color="info"
                    sx={{ px: 1 }}
                />
            </Stack>

            <Card elevation={3} sx={{ mb: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" color="text.secondary">
                                {translations.welcome}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {translations.registrations}
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                            <Typography variant="h5" sx={{ color: 'white' }}>
                                {total}
                            </Typography>
                        </Avatar>
                    </Stack>
                </CardContent>
            </Card>

            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ height: 'calc(100vh - 280px)' }}>
                    {loading && <LinearProgress sx={{ mb: -0.5 }} />}
                    <DataGrid
                        rows={registrations}
                        columns={columns}
                        loading={loading}
                        pageSizeOptions={[10, 25, 50]}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        rowCount={total}
                        onPaginationModelChange={setPaginationModel}
                        checkboxSelection
                        onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
                        slots={{ toolbar: CustomToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: 'primary.light',
                                color: 'black',
                                borderRadius: 0
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default Registrations;